import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './useAuth'
import { apiService } from '../services/api'
import { Partner } from '../types/maintenance'

interface UsePartnersOptions {
  autoFetch?: boolean
  cacheResults?: boolean
  cacheTimeout?: number // in milliseconds
  status?: string
  region?: string
  specialization?: string
  includeInactive?: boolean
}

interface UsePartnersReturn {
  partners: Partner[]
  loading: boolean
  error: string | null
  
  // Actions
  refreshPartners: () => Promise<void>
  getPartnerById: (id: string) => Promise<Partner | null>
  getActivePartners: () => Partner[]
  
  // Computed values
  hasPartners: boolean
  activePartners: Partner[]
  partnersByRegion: Record<string, Partner[]>
}

// Simple cache implementation
const partnerCache = new Map<string, { data: Partner[]; timestamp: number }>()

export function usePartners(options: UsePartnersOptions = {}): UsePartnersReturn {
  const { 
    autoFetch = true, 
    cacheResults = true, 
    cacheTimeout = 10 * 60 * 1000, // 10 minutes default
    status,
    region,
    specialization,
    includeInactive = false
  } = options
  
  const { isAuthenticated } = useAuth()
  
  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create cache key based on filters
  const cacheKey = useMemo(() => {
    const filters = [
      status || 'all',
      region || 'all',
      specialization || 'all',
      includeInactive ? 'inactive' : 'active'
    ].join('_')
    return `partners_${filters}`
  }, [status, region, specialization, includeInactive])

  const fetchPartners = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) {
      return
    }

    // Check cache first
    if (cacheResults && !forceRefresh) {
      const cached = partnerCache.get(cacheKey)
      if (cached && Date.now() - cached.timestamp < cacheTimeout) {
        setPartners(cached.data)
        return
      }
    }

    setLoading(true)
    setError(null)

    try {
      const params: {
        status?: string
        region?: string
        specialization?: string
        includeInactive?: boolean
      } = {}

      if (status) params.status = status
      if (region) params.region = region
      if (specialization) params.specialization = specialization
      if (includeInactive) params.includeInactive = true

      const response = await apiService.getPartners(params)
      setPartners(response)

      // Cache the results
      if (cacheResults) {
        partnerCache.set(cacheKey, {
          data: response,
          timestamp: Date.now()
        })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch partners'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, status, region, specialization, includeInactive, cacheKey, cacheResults, cacheTimeout])

  const getPartnerById = useCallback(async (id: string): Promise<Partner | null> => {
    // First check if we already have it in our loaded partners
    const existingPartner = partners.find(p => p.partnerId === id)
    if (existingPartner) {
      return existingPartner
    }

    // If not found, fetch from API
    try {
      return await apiService.getPartnerById(id)
    } catch (err) {
      console.error(`Failed to get partner ${id}:`, err)
      return null
    }
  }, [partners])

  // Computed values
  const hasPartners = useMemo(() => partners.length > 0, [partners.length])
  
  const activePartners = useMemo(() => {
    return partners.filter(partner => partner.status === 'ativo')
  }, [partners])

  const getActivePartners = useCallback(() => {
    return activePartners
  }, [activePartners])

  const partnersByRegion = useMemo(() => {
    const grouped: Record<string, Partner[]> = {}
    
    partners.forEach(partner => {
      const regions = partner.regionsCovered.split(';').map(r => r.trim())
      regions.forEach(region => {
        if (!grouped[region]) {
          grouped[region] = []
        }
        grouped[region].push(partner)
      })
    })
    
    return grouped
  }, [partners])

  const refreshPartners = useCallback(() => {
    return fetchPartners(true) // Force refresh
  }, [fetchPartners])

  // Auto-fetch when dependencies change
  useEffect(() => {
    if (autoFetch && isAuthenticated) {
      fetchPartners()
    }
  }, [autoFetch, isAuthenticated, fetchPartners])

  // Clean up cache on unmount
  useEffect(() => {
    return () => {
      // Optional: Clean up old cache entries
      const now = Date.now()
      for (const [key, value] of partnerCache.entries()) {
        if (now - value.timestamp > cacheTimeout * 2) {
          partnerCache.delete(key)
        }
      }
    }
  }, [cacheTimeout])

  return {
    partners,
    loading,
    error,
    
    // Actions
    refreshPartners,
    getPartnerById,
    getActivePartners,
    
    // Computed values
    hasPartners,
    activePartners,
    partnersByRegion
  }
}

/**
 * Hook for getting a single partner by ID
 */
export function usePartner(id?: string) {
  const [partner, setPartner] = useState<Partner | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPartner = useCallback(async (partnerId: string) => {
    if (!partnerId) return

    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getPartnerById(partnerId)
      setPartner(response)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch partner'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (id) {
      fetchPartner(id)
    }
  }, [id, fetchPartner])

  return {
    partner,
    loading,
    error,
    fetchPartner
  }
}